import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    CardContent,
    CircularProgress,
    Divider,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import { alpha } from "@mui/material/styles";

import { PRIMARY_COLOR } from "../util/constants";
import { handleResponse } from "../util/integration";
import {
    bidAPI,
    fetchAuctionAPI,
    fetchAuctionStatusAPI,
} from "../api/auction_api";
import { fetchPublicLeadAPI } from "../api/lead_api";
import {
    fetchUserCanBuylAPI,
    fetchUserProfileAPI,
} from "../api/user_api";
import {
    getIncreasingPollingDelay,
    getPollingDelay,
    POLLING_SPEED,
    usePolling,
} from "../util/polling";
import StaticMap from "../fragments/StaticMap";

const MemoizedStaticMap = memo(StaticMap);

const AUCTION_STATUS = Object.freeze({
    created: "CREATED",
    active: "ACTIVE",
    terminated: "TERMINATED",
    aborted: "ABORTED",
});

const INFO_DIALOG = Object.freeze({
    details: "details",
    comment: "comment",
    documents: "documents",
    seller: "seller",
});

const VIEW_STATE = Object.freeze({
    loading: "loading",
    error: "error",
    unavailable: "unavailable",
    ready: "ready",
});

const SELLER_VIEW_STATE = Object.freeze({
    loading: "loading",
    unavailable: "unavailable",
    ready: "ready",
});

const STATUS_REQUEST_TYPE = Object.freeze({
    initial: "initial",
    interactive: "interactive",
    polling: "polling",
});

const LABEL = Object.freeze({
    auctionId: "ID",
    bid: "Bieten",
    auction: "Auktion",
    details: "Details",
    comment: "Kommentar",
    seller: "Anbieter",
    currentHighestBid: "Aktuelles Höchstgebot",
    noBid: "Kein Gebot",
    yourBid: "Ihr Gebot",
    created: "Noch nicht gestartet",
    active: "Aktiv",
    terminated: "Beendet",
    aborted: "Abgebrochen",
    auctionStart: "Auktionsstart",
    auctionEnd: "Auktionsende",
    category: "Kategorie",
    itemType: "Art",
    zipCode: "Postleitzahl",
    status: "Status",
    b2b: "B2B",
    b2c: "B2C",
    minimumBid: "Mindestgebot",
    documents: "Dokumente",
    auctionInformation: "Auktionsdaten",
    sellerProfile: "Anbieterprofil",
    publicComment: "Öffentlicher Kommentar",
    pdfDocument: "PDF-Dokument",
    documentMissing: "Nicht hinterlegt",
    download: "Herunterladen",
    operatorTerms: "AGB des Betreibers",
    sellerTerms: "AGB des Anbieters",
    operatorPrivacyPolicy: "Datenschutzerklärung des Betreibers",
    sellerPrivacyPolicy: "Datenschutzerklärung des Anbieters",
    sellerBusinessName: "Unternehmen",
    sellerName: "Name",
    sellerUsername: "Benutzername",
    sellerLocation: "Standort",
    sellerCountry: "Land",
    sellerProfileLoading: "Anbieterprofil wird geladen …",
    sellerProfileUnavailable: "Das Anbieterprofil ist nicht verfügbar.",
    closeDialog: "Dialog schließen",
    refreshStatus: "Auktionsstatus aktualisieren",
    defaultTitle: "Auktion",
    information: "Informationen",
});

const MESSAGE = Object.freeze({
    highestBidder: "Ihr Gebot wurde erfolgreich abgegeben.",
    outbid: "Sie wurden überboten.",
    bidNotAccepted: "Ihr Gebot konnte nicht berücksichtigt werden.",
    ownAuction: "Für eigene Auktionen können keine Gebote abgegeben werden.",
    noComment: "Kein Kommentar vorhanden.",
    documentUnavailable: "Das Dokument ist nicht verfügbar.",
    documentDownloadFailed: "Das Dokument konnte nicht heruntergeladen werden.",
    notAvailable: "Nicht verfügbar",
    missingLead: "Der zugehörige Lead konnte nicht ermittelt werden.",
    missingSeller: "Der Anbieter konnte nicht ermittelt werden.",
    auctionLoadFailed: "Die Auktion konnte nicht geladen werden.",
    initialStatusLoadFailed:
        "Der aktuelle Auktionsstatus konnte nicht geladen werden. Die angezeigten Daten sind möglicherweise nicht aktuell.",
    liveStatusUnavailable:
        "Die Live-Aktualisierung ist derzeit unterbrochen. Die angezeigten Auktionsdaten können veraltet sein.",
});

const CONFIG = Object.freeze({
    statusPollingMediumThresholdMs: 2 * 60 * 1_000,
    statusPollingFastThresholdMs: 20 * 1_000,
    statusPollingFailureThreshold: 3,
    defaultMapLatitude: 52.52,
    defaultMapLongitude: 13.405,
    mapZoom: 5,
    mapHeight: 150,
});

const VALUE = Object.freeze({
    empty: "",
    fallback: "–",
    documentFallback: "dokument",
    documentPrefix: "auktion",
    currencyCode: "EUR",
    currencyLocale: "de-DE",
    dateLocale: "de-DE",
    commentSeparator: "$",
    lineBreak: "\n",
    enterKey: "Enter",
    pdfMimeType: "application/pdf",
    downloadElement: "a",
});

const UI = Object.freeze({
    boolean: {
        true: true,
    },
    component: {
        span: "span",
        div: "div",
    },
    variant: {
        outlined: "outlined",
        caption: "caption",
        body1: "body1",
        body2: "body2",
        h5: "h5",
        h6: "h6",
        standard: "standard",
        determinate: "determinate",
    },
    color: {
        secondaryText: "text.secondary",
    },
    severity: {
        error: "error",
        warning: "warning",
    },
    size: {
        small: "small",
    },
    dialog: {
        scrollPaper: "paper",
        small: "sm",
        medium: "md",
    },
    grid: {
        full: 12,
        half: 6,
        bidInput: 9,
        contentSpacing: 3,
        detailsSpacing: 2.25,
    },
    stack: {
        row: "row",
        center: "center",
        spaceBetween: "space-between",
        cardSpacing: 1.25,
        documentSpacing: 2,
    },
    progress: {
        sellerSize: 22,
        auctionSize: 12,
        auctionThickness: 25,
    },
});

const RESPONSIVE_DOCUMENT_DIRECTION = Object.freeze({
    xs: "column",
    sm: "row",
});

const RESPONSIVE_DOCUMENT_ALIGNMENT = Object.freeze({
    xs: "stretch",
    sm: "center",
});

const DOCUMENT_DEFINITIONS = Object.freeze([
    {
        key: "operatorTerms",
        label: LABEL.operatorTerms,
        fileName: "betreiber-agb.pdf",
    },
    {
        key: "sellerTerms",
        label: LABEL.sellerTerms,
        fileName: "anbieter-agb.pdf",
    },
    {
        key: "operatorPrivacyPolicy",
        label: LABEL.operatorPrivacyPolicy,
        fileName: "betreiber-datenschutz.pdf",
    },
    {
        key: "sellerPrivacyPolicy",
        label: LABEL.sellerPrivacyPolicy,
        fileName: "anbieter-datenschutz.pdf",
    },
]);

const DIALOG_DEFINITIONS = Object.freeze({
    [INFO_DIALOG.details]: {
        title: LABEL.details,
        Icon: InfoOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
    [INFO_DIALOG.comment]: {
        title: LABEL.comment,
        Icon: CommentOutlinedIcon,
        maxWidth: UI.dialog.small,
    },
    [INFO_DIALOG.documents]: {
        title: LABEL.documents,
        Icon: DescriptionOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
    [INFO_DIALOG.seller]: {
        title: LABEL.seller,
        Icon: StorefrontOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
});

const DEFAULT_DIALOG_DEFINITION = Object.freeze({
    title: LABEL.information,
    Icon: InfoOutlinedIcon,
    maxWidth: UI.dialog.small,
});

const STYLE = Object.freeze({
    paper: {
        display: "block",
        width: "100%",
        height: "100%",
        minHeight: 320,
        overflow: "hidden",
        borderRadius: 3,
    },
    titleBar: {
        minHeight: "42px",
        padding: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        boxSizing: "border-box",
    },
    titleText: {
        fontFamily: "Arial, Helvetica",
        lineHeight: 1.25,
        minWidth: 0,
        wordBreak: "break-word",
    },
    badgeContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        flexWrap: "wrap",
        flexShrink: 0,
    },
    headerBadge: {
        flexShrink: 0,
        padding: "5px 10px",
        borderRadius: "999px",
        fontSize: "0.75rem",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
    },
    content: {
        padding: "25px",
    },
    actionContent: {
        padding: "25px",
        paddingTop: 0,
    },
    statusLabel: {
        fontSize: 14,
    },
    statusValue: {
        minHeight: "32px",
    },
    minimumBid: {
        minHeight: "24px",
    },
    timeContainer: {
        marginTop: "5px",
    },
    progress: {
        marginLeft: "5px",
    },
    timeText: {
        display: "inline-block",
        marginLeft: "5px",
        fontSize: 14,
    },
    infoActions: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
    },
    infoButton: {
        color: "grey",
    },
    statusWarning: {
        borderRadius: 0,
    },
    terminalError: {
        marginTop: 1,
    },
    dialogPaper: {
        borderRadius: 3,
        overflow: "hidden",
    },
    dialogTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 3,
        py: 1.5,
    },
    dialogTitleContent: {
        minWidth: 0,
    },
    dialogTitleIcon: {
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
    },
    dialogTitleText: {
        fontFamily: "Arial, Helvetica",
        fontWeight: 700,
        overflowWrap: "anywhere",
    },
    dialogContent: {
        px: {
            xs: 2,
            sm: 3,
        },
        py: 3,
    },
    overviewPaper: {
        display: "block",
        overflow: "hidden",
        borderRadius: 3,
    },
    overviewContent: {
        padding: {
            xs: "18px",
            sm: "24px",
        },
        "&:last-child": {
            paddingBottom: {
                xs: "18px",
                sm: "24px",
            },
        },
    },
    cardHeader: {
        marginBottom: "18px",
    },
    cardIcon: {
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
    },
    cardTitle: {
        fontFamily: "Arial, Helvetica",
        fontSize: "1rem",
        fontWeight: 700,
    },
    infoLabel: {
        display: "block",
        marginBottom: "3px",
        fontWeight: 600,
    },
    infoValue: {
        overflowWrap: "anywhere",
        lineHeight: 1.45,
    },
    commentText: {
        margin: 0,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        lineHeight: 1.65,
    },
    documentRow: {
        paddingTop: "10px",
        paddingBottom: "10px",
    },
    documentButton: {
        alignSelf: {
            xs: "stretch",
            sm: "center",
        },
        borderRadius: 2,
    },
    sellerLoading: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
    },
});

const createColorStyles = (primaryColor) => Object.freeze({
    paper: {
        ...STYLE.paper,
        borderColor: alpha(primaryColor, 0.14),
        boxShadow: `0 12px 30px ${alpha(primaryColor, 0.06)}`,
    },
    titleBar: {
        ...STYLE.titleBar,
        backgroundColor: primaryColor,
        color: "white",
    },
    headerBadge: {
        ...STYLE.headerBadge,
        border: "1px solid rgba(255, 255, 255, 0.38)",
        backgroundColor: "rgba(255, 255, 255, 0.18)",
        color: "white",
    },
    dialogTitle: {
        ...STYLE.dialogTitle,
        backgroundColor: primaryColor,
        color: "white",
    },
    dialogCloseButton: {
        color: "white",
    },
    dialogTitleIcon: {
        ...STYLE.dialogTitleIcon,
        backgroundColor: "rgba(255, 255, 255, 0.18)",
        color: "white",
    },
    dialogContent: {
        ...STYLE.dialogContent,
        backgroundColor: alpha(primaryColor, 0.025),
    },
    overviewPaper: {
        ...STYLE.overviewPaper,
        borderColor: alpha(primaryColor, 0.14),
        boxShadow: `0 12px 30px ${alpha(primaryColor, 0.06)}`,
    },
    cardIcon: {
        ...STYLE.cardIcon,
        backgroundColor: alpha(primaryColor, 0.09),
        color: primaryColor,
    },
    primaryAction: {
        color: primaryColor,
    },
    actionButton: {
        color: primaryColor,
    },
    documentButton: {
        ...STYLE.documentButton,
        borderColor: primaryColor,
        color: primaryColor,
    },
    progress: {
        ...STYLE.progress,
        color: primaryColor,
    },
});

const COLOR_STYLE = createColorStyles(PRIMARY_COLOR);
const DIALOG_PAPER_PROPS = Object.freeze({ sx: STYLE.dialogPaper });

const normalizeCanBuy = (response) => {
    if (response !== null && typeof response === "object") {
        return Boolean(
            response.canBuy
            ?? response.allowed
            ?? response.value,
        );
    }

    return Boolean(response);
};

const displayValue = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    return value;
};

const hasDocumentData = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    return (
        value !== null
        && typeof value === "object"
        && Array.isArray(value.data)
        && value.data.length > 0
    );
};

const normalizeDocumentBytes = (value) => {
    if (Array.isArray(value)) {
        return new Uint8Array(value);
    }

    if (
        value !== null
        && typeof value === "object"
        && Array.isArray(value.data)
    ) {
        return new Uint8Array(value.data);
    }

    if (typeof value !== "string") {
        return null;
    }

    const base64Value = value
        .replace(/^data:[^;]+;base64,/, VALUE.empty)
        .replaceAll(/\s/g, VALUE.empty)
        .replaceAll("-", "+")
        .replaceAll("_", "/");

    if (base64Value === VALUE.empty) {
        return null;
    }

    const paddedBase64 = base64Value.padEnd(
        Math.ceil(base64Value.length / 4) * 4,
        "=",
    );

    try {
        const binaryValue = window.atob(paddedBase64);

        return Uint8Array.from(
            binaryValue,
            (character) => character.charCodeAt(0),
        );
    } catch {
        return null;
    }
};

const downloadDocument = (value, fileName) => {
    const documentBytes = normalizeDocumentBytes(value);

    if (documentBytes === null || documentBytes.length === 0) {
        throw new Error(MESSAGE.documentUnavailable);
    }

    const objectUrl = window.URL.createObjectURL(
        new Blob(
            [documentBytes],
            { type: VALUE.pdfMimeType },
        ),
    );

    const downloadLink = document.createElement(VALUE.downloadElement);
    downloadLink.href = objectUrl;
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();

    window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
    }, 0);
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return `${value} ${VALUE.currencyCode}`;
    }

    return new Intl.NumberFormat(
        VALUE.currencyLocale,
        {
            style: "currency",
            currency: VALUE.currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        },
    ).format(numericValue);
};

const formatDateTime = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat(
        VALUE.dateLocale,
        {
            dateStyle: "medium",
            timeStyle: "medium",
        },
    ).format(date);
};

const formatComment = (comment) => {
    if (comment === null || comment === undefined || comment === VALUE.empty) {
        return MESSAGE.noComment;
    }

    return String(comment).replaceAll(
        VALUE.commentSeparator,
        VALUE.lineBreak,
    );
};

const formatSellerName = (sellerProfile) => {
    const name = [
        sellerProfile?.firstName,
        sellerProfile?.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    return displayValue(name);
};

const formatSellerLocation = (sellerProfile) => {
    const location = [
        sellerProfile?.zipCode,
        sellerProfile?.town,
    ]
        .filter(Boolean)
        .join(" ");

    return displayValue(location);
};

const normalizeCoordinate = (value, minimum, maximum) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return null;
    }

    const numericValue = Number(value);

    if (
        !Number.isFinite(numericValue)
        || numericValue < minimum
        || numericValue > maximum
    ) {
        return null;
    }

    return numericValue;
};

const getMapCoordinates = (lead) => {
    const latitude = normalizeCoordinate(lead?.latitude, -90, 90);
    const longitude = normalizeCoordinate(lead?.longitude, -180, 180);

    if (latitude === null || longitude === null) {
        return {
            latitude: CONFIG.defaultMapLatitude,
            longitude: CONFIG.defaultMapLongitude,
        };
    }

    return { latitude, longitude };
};

const toTimestamp = (value) => {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
        return numericValue;
    }

    const parsedValue = new Date(value).getTime();

    if (Number.isNaN(parsedValue)) {
        return null;
    }

    return parsedValue;
};

const getAuctionEndTimestamp = (auction) => {
    const startTimestamp = toTimestamp(auction?.startTimestamp);
    const duration = Number(auction?.duration);

    if (startTimestamp === null || !Number.isFinite(duration)) {
        return null;
    }

    return startTimestamp + duration;
};

const getStatusPollingDelay = ({
    status,
    auction,
    pollingStartedAt,
}) => {
    if (
        status === AUCTION_STATUS.terminated
        || status === AUCTION_STATUS.aborted
    ) {
        return null;
    }

    if (status !== AUCTION_STATUS.active) {
        return getPollingDelay(POLLING_SPEED.slow);
    }

    const endTimestamp = getAuctionEndTimestamp(auction);

    if (endTimestamp === null) {
        return getPollingDelay(POLLING_SPEED.slow);
    }

    return getIncreasingPollingDelay({
        endTimestamp,
        mediumThresholdMs: CONFIG.statusPollingMediumThresholdMs,
        fastThresholdMs: CONFIG.statusPollingFastThresholdMs,
        pollingStartedAt,
    });
};

const calculateProgress = (startTimestamp, duration) => {
    const normalizedStart = toTimestamp(startTimestamp);
    const normalizedDuration = Number(duration);

    if (
        normalizedStart === null
        || !Number.isFinite(normalizedDuration)
        || normalizedDuration <= 0
    ) {
        return 0;
    }

    const elapsedTime = Date.now() - normalizedStart;

    if (elapsedTime <= 0) {
        return 0;
    }

    if (elapsedTime >= normalizedDuration) {
        return 100;
    }

    return Math.round((elapsedTime / normalizedDuration) * 100);
};

const getAuctionStatusValue = (auctionStatus, auction) => (
    auctionStatus?.auctionStatus
    ?? auction?.status
    ?? null
);

const getIsHighestBidder = (auctionStatus) => Boolean(
    auctionStatus?.highestBidder
    ?? auctionStatus?.isHighestBidder,
);

const getCategoryName = (lead) => (
    lead?.category?.name
    ?? lead?.categoryName
    ?? null
);

const idsAreEqual = (firstId, secondId) => {
    if (
        firstId === null
        || firstId === undefined
        || secondId === null
        || secondId === undefined
    ) {
        return false;
    }

    return String(firstId) === String(secondId);
};

const buildAuctionTitle = (lead) => {
    const categoryName = getCategoryName(lead);
    const zipCode = lead?.zipCode;
    const hasZipCode = zipCode !== null
        && zipCode !== undefined
        && zipCode !== VALUE.empty;
    const hasCategoryName = categoryName !== null
        && categoryName !== undefined
        && categoryName !== VALUE.empty;

    if (hasZipCode && hasCategoryName) {
        return `${zipCode}: ${categoryName}`;
    }

    return categoryName ?? zipCode ?? LABEL.defaultTitle;
};

const getStatusLabel = (status) => {
    switch (status) {
        case AUCTION_STATUS.created:
            return LABEL.created;
        case AUCTION_STATUS.active:
            return LABEL.active;
        case AUCTION_STATUS.terminated:
            return LABEL.terminated;
        case AUCTION_STATUS.aborted:
            return LABEL.aborted;
        default:
            return MESSAGE.notAvailable;
    }
};

const getStatusDisplayValue = (status, highestBid) => {
    switch (status) {
        case AUCTION_STATUS.created:
            return LABEL.created;
        case AUCTION_STATUS.active:
            return highestBid === null || highestBid === undefined
                ? LABEL.noBid
                : formatCurrency(highestBid);
        case AUCTION_STATUS.terminated:
            return LABEL.terminated;
        case AUCTION_STATUS.aborted:
            return LABEL.aborted;
        default:
            return MESSAGE.notAvailable;
    }
};

const getStatusTimeText = (status, auction) => {
    if (status === AUCTION_STATUS.created) {
        return `${LABEL.auctionStart}: ${formatDateTime(auction?.startTimestamp)}`;
    }

    if (status === AUCTION_STATUS.active) {
        return `${LABEL.auctionEnd}: ${formatDateTime(getAuctionEndTimestamp(auction))}`;
    }

    return VALUE.empty;
};

const getLeadTypeLabel = (lead) => {
    if (lead?.type === LABEL.b2b) {
        return LABEL.b2b;
    }

    if (lead?.type === LABEL.b2c) {
        return LABEL.b2c;
    }

    return displayValue(lead?.type);
};

const isPositiveInteger = (value) => {
    const numericValue = Number(value);

    return Number.isSafeInteger(numericValue) && numericValue > 0;
};

const buildDocumentFilePrefix = (auctionId) => (
    `${VALUE.documentPrefix}-${auctionId ?? VALUE.documentFallback}`
);

const buildDocumentFileName = (filePrefix, fileName) => (
    `${filePrefix}-${fileName}`
);

const useMountedRef = () => {
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return isMounted;
};

const useAuctionResource = ({
    accessToken,
    auctionId,
    userId,
    showMessage,
}) => {
    const [auction, setAuction] = useState(null);
    const [lead, setLead] = useState(null);
    const [isAuctionLoading, setIsAuctionLoading] = useState(true);
    const [isLeadLoading, setIsLeadLoading] = useState(false);
    const [hasAuctionError, setHasAuctionError] = useState(false);
    const [hasLeadError, setHasLeadError] = useState(false);
    const [auctionErrorMessage, setAuctionErrorMessage] = useState(VALUE.empty);

    useEffect(() => {
        let requestIsActive = true;

        setAuction(null);
        setLead(null);
        setHasAuctionError(false);
        setHasLeadError(false);
        setAuctionErrorMessage(VALUE.empty);
        setIsAuctionLoading(true);
        setIsLeadLoading(false);

        handleResponse(
            fetchAuctionAPI(accessToken, auctionId, userId),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setAuction(response);
                setAuctionErrorMessage(VALUE.empty);
                setIsAuctionLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setAuction(null);
                setHasAuctionError(true);
                setAuctionErrorMessage(message || MESSAGE.auctionLoadFailed);
                setIsAuctionLoading(false);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, auctionId, userId]);

    useEffect(() => {
        if (auction === null) {
            return undefined;
        }

        if (auction.leadId === null || auction.leadId === undefined) {
            setLead(null);
            setHasLeadError(true);
            setIsLeadLoading(false);
            showMessage(MESSAGE.missingLead);
            return undefined;
        }

        let requestIsActive = true;

        setLead(null);
        setHasLeadError(false);
        setIsLeadLoading(true);

        handleResponse(
            fetchPublicLeadAPI(accessToken, auction.leadId),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setLead(response);
                setIsLeadLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setLead(null);
                setHasLeadError(true);
                setIsLeadLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, auction, showMessage]);

    return {
        auction,
        lead,
        isLoading: isAuctionLoading || isLeadLoading,
        hasAuctionError,
        hasLeadError,
        auctionErrorMessage,
    };
};

const useBuyerPermission = ({
    userId,
    username,
    accessToken,
    showMessage,
}) => {
    const [canBuy, setCanBuy] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let requestIsActive = true;

        setIsLoading(true);

        handleResponse(
            fetchUserCanBuylAPI(userId, username, accessToken),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setCanBuy(normalizeCanBuy(response));
                setIsLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setCanBuy(false);
                setIsLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, showMessage, userId, username]);

    return {
        canBuy,
        isLoading,
    };
};

const useAuctionStatusController = ({
    accessToken,
    auctionId,
    userId,
    auction,
    showMessage,
}) => {
    const isMounted = useMountedRef();
    const [auctionStatus, setAuctionStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [warningMessage, setWarningMessage] = useState(VALUE.empty);
    const requestInProgress = useRef(false);
    const consecutivePollingFailures = useRef(0);
    const wasHighestBidder = useRef(false);

    const loadAuctionStatus = useCallback((requestType) => (
        new Promise((resolve) => {
            const finishRequest = () => {
                requestInProgress.current = false;
                resolve();
            };

            if (requestInProgress.current) {
                resolve();
                return;
            }

            requestInProgress.current = true;

            handleResponse(
                fetchAuctionStatusAPI(accessToken, auctionId, userId),
                (response) => {
                    if (!isMounted.current) {
                        finishRequest();
                        return;
                    }

                    const nextStatus = response?.auctionStatus;
                    const nextIsHighestBidder = getIsHighestBidder(response);

                    if (
                        wasHighestBidder.current
                        && !nextIsHighestBidder
                        && nextStatus === AUCTION_STATUS.active
                    ) {
                        showMessage(MESSAGE.outbid);
                    }

                    wasHighestBidder.current = nextIsHighestBidder;
                    consecutivePollingFailures.current = 0;
                    setWarningMessage(VALUE.empty);
                    setAuctionStatus(response);
                    setIsLoading(false);
                    finishRequest();
                },
                (message) => {
                    if (!isMounted.current) {
                        finishRequest();
                        return;
                    }

                    setIsLoading(false);

                    if (requestType === STATUS_REQUEST_TYPE.polling) {
                        const nextFailureCount = consecutivePollingFailures.current + 1;
                        consecutivePollingFailures.current = nextFailureCount;

                        if (nextFailureCount >= CONFIG.statusPollingFailureThreshold) {
                            setWarningMessage(MESSAGE.liveStatusUnavailable);
                        }
                    } else {
                        consecutivePollingFailures.current = 0;

                        const fallbackMessage = requestType === STATUS_REQUEST_TYPE.initial
                            ? MESSAGE.initialStatusLoadFailed
                            : MESSAGE.liveStatusUnavailable;
                        const resolvedMessage = message || fallbackMessage;

                        setWarningMessage(resolvedMessage);

                        if (requestType === STATUS_REQUEST_TYPE.interactive) {
                            showMessage(resolvedMessage);
                        }
                    }

                    finishRequest();
                },
            );
        })
    ), [accessToken, auctionId, isMounted, showMessage, userId]);

    useEffect(() => {
        setAuctionStatus(null);
        setIsLoading(true);
        setWarningMessage(VALUE.empty);
        consecutivePollingFailures.current = 0;
        wasHighestBidder.current = false;

        void loadAuctionStatus(STATUS_REQUEST_TYPE.initial);
    }, [loadAuctionStatus]);

    const currentStatus = getAuctionStatusValue(auctionStatus, auction);
    const shouldPollStatus = currentStatus === null
        || currentStatus === AUCTION_STATUS.created
        || currentStatus === AUCTION_STATUS.active;

    usePolling({
        enabled: shouldPollStatus,
        restartKey: auctionId,
        poll: () => loadAuctionStatus(STATUS_REQUEST_TYPE.polling),
        getDelay: ({ pollingStartedAt }) => getStatusPollingDelay({
            status: currentStatus,
            auction,
            pollingStartedAt,
        }),
    });

    const refresh = useCallback(() => {
        void loadAuctionStatus(STATUS_REQUEST_TYPE.interactive);
    }, [loadAuctionStatus]);

    const markAsHighestBidder = useCallback(() => {
        wasHighestBidder.current = true;
    }, []);

    return {
        auctionStatus,
        currentStatus,
        highestBid: auctionStatus?.maxBid ?? null,
        isHighestBidder: getIsHighestBidder(auctionStatus),
        isLoading,
        warningMessage,
        refresh,
        markAsHighestBidder,
    };
};

const useSellerProfileController = ({
    accessToken,
    sellerId,
    openDialog,
    showMessage,
}) => {
    const [sellerProfile, setSellerProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (openDialog !== INFO_DIALOG.seller) {
            return undefined;
        }

        if (sellerId === null || sellerId === undefined) {
            setSellerProfile(null);
            setHasError(true);
            setIsLoading(false);
            showMessage(MESSAGE.missingSeller);
            return undefined;
        }

        let requestIsActive = true;

        setSellerProfile(null);
        setHasError(false);
        setIsLoading(true);

        handleResponse(
            fetchUserProfileAPI(sellerId, accessToken),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setSellerProfile(response);
                setIsLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setSellerProfile(null);
                setHasError(true);
                setIsLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, openDialog, sellerId, showMessage]);

    return {
        sellerProfile,
        isLoading,
        hasError,
    };
};

const useDocumentDownload = (showMessage) => useCallback((value, fileName) => {
    try {
        downloadDocument(value, fileName);
    } catch (error) {
        showMessage(error?.message || MESSAGE.documentDownloadFailed);
    }
}, [showMessage]);

const useBidController = ({
    accessToken,
    auction,
    userId,
    canBuy,
    canBuyLoading,
    currentStatus,
    highestBid,
    statusLoading,
    isOwnAuction,
    refreshStatus,
    markAsHighestBidder,
    showMessage,
}) => {
    const [currentBid, setCurrentBid] = useState(VALUE.empty);
    const isAuctionActive = currentStatus === AUCTION_STATUS.active;
    const minimumAllowedBid = highestBid === null || highestBid === undefined
        ? Number(auction?.minBid)
        : Number(highestBid) + 1;
    const numericCurrentBid = Number(currentBid);
    const isCurrentBidValid = isPositiveInteger(currentBid)
        && (
            !Number.isFinite(minimumAllowedBid)
            || numericCurrentBid >= minimumAllowedBid
        );
    const inputDisabled = !isAuctionActive || isOwnAuction;
    const buttonDisabled = !isAuctionActive
        || isOwnAuction
        || !isCurrentBidValid
        || !canBuy
        || canBuyLoading
        || statusLoading;

    const submit = useCallback(() => {
        if (
            auction === null
            || isOwnAuction
            || !isAuctionActive
            || !canBuy
            || !isCurrentBidValid
        ) {
            return;
        }

        handleResponse(
            bidAPI(accessToken, auction.id, userId, numericCurrentBid),
            (accepted) => {
                if (accepted) {
                    markAsHighestBidder();
                    showMessage(MESSAGE.highestBidder);
                } else {
                    showMessage(MESSAGE.bidNotAccepted);
                }

                setCurrentBid(VALUE.empty);
                refreshStatus();
            },
            (message) => {
                showMessage(message);
            },
        );
    }, [
        accessToken,
        auction,
        canBuy,
        isAuctionActive,
        isCurrentBidValid,
        isOwnAuction,
        markAsHighestBidder,
        numericCurrentBid,
        refreshStatus,
        showMessage,
        userId,
    ]);

    const handleChange = useCallback((event) => {
        const value = event.target.value;
        const isValidInput = value === VALUE.empty || /^\d+$/.test(value);

        if (isValidInput) {
            setCurrentBid(value);
        }
    }, []);

    const handleKeyDown = useCallback((event) => {
        if (event.key === VALUE.enterKey) {
            submit();
        }
    }, [submit]);

    return {
        currentBid,
        isAuctionActive,
        inputDisabled,
        buttonDisabled,
        helperText: isOwnAuction ? MESSAGE.ownAuction : VALUE.empty,
        submit,
        handleChange,
        handleKeyDown,
    };
};

const useAuctionDialogController = ({
    auction,
    lead,
    currentStatus,
    sellerId,
    accessToken,
    showMessage,
    handleDocumentDownload,
}) => {
    const [openDialog, setOpenDialog] = useState(null);
    const seller = useSellerProfileController({
        accessToken,
        sellerId,
        openDialog,
        showMessage,
    });

    const openDetails = useCallback(() => {
        setOpenDialog(INFO_DIALOG.details);
    }, []);

    const openComment = useCallback(() => {
        setOpenDialog(INFO_DIALOG.comment);
    }, []);

    const openDocuments = useCallback(() => {
        setOpenDialog(INFO_DIALOG.documents);
    }, []);

    const openSeller = useCallback(() => {
        setOpenDialog(INFO_DIALOG.seller);
    }, []);

    const close = useCallback(() => {
        setOpenDialog(null);
    }, []);

    const documentFilePrefix = buildDocumentFilePrefix(auction?.id);

    const documents = useMemo(() => DOCUMENT_DEFINITIONS.map((definition) => {
        const value = auction?.[definition.key];
        const fileName = buildDocumentFileName(
            documentFilePrefix,
            definition.fileName,
        );

        const available = hasDocumentData(value);

        return {
            key: definition.key,
            title: definition.label,
            disabled: !available,
            typeText: available
                ? LABEL.pdfDocument
                : LABEL.documentMissing,
            downloadLabel: LABEL.download,
            onDownload: () => handleDocumentDownload(value, fileName),
        };
    }), [auction, documentFilePrefix, handleDocumentDownload]);

const details = useMemo(() => ({
    title: LABEL.auctionInformation,
    Icon: GavelIcon,
    rows: [
        {
            key: LABEL.auctionId,
            label: LABEL.auctionId,
            value: displayValue(auction?.id),
            sm: UI.grid.full,
        },
        {
            key: LABEL.category,
            label: LABEL.category,
            value: displayValue(getCategoryName(lead)),
            sm: UI.grid.full,
        },
        {
            key: LABEL.itemType,
            label: LABEL.itemType,
            value: getLeadTypeLabel(lead),
            sm: UI.grid.half,
        },
        {
            key: LABEL.zipCode,
            label: LABEL.zipCode,
            value: displayValue(lead?.zipCode),
            sm: UI.grid.half,
        },
        {
            key: LABEL.status,
            label: LABEL.status,
            value: getStatusLabel(currentStatus),
            sm: UI.grid.half,
        },
        {
            key: LABEL.minimumBid,
            label: LABEL.minimumBid,
            value: formatCurrency(auction?.minBid),
            sm: UI.grid.half,
        },
        {
            key: LABEL.auctionStart,
            label: LABEL.auctionStart,
            value: formatDateTime(auction?.startTimestamp),
            sm: UI.grid.half,
        },
        {
            key: LABEL.auctionEnd,
            label: LABEL.auctionEnd,
            value: formatDateTime(getAuctionEndTimestamp(auction)),
            sm: UI.grid.half,
        },
    ],
}), [auction, currentStatus, lead]);

    const comment = useMemo(() => ({
        title: LABEL.publicComment,
        Icon: CommentOutlinedIcon,
        text: formatComment(lead?.publicComment),
    }), [lead]);

    const sellerViewState = seller.isLoading
        ? SELLER_VIEW_STATE.loading
        : seller.hasError || seller.sellerProfile === null
            ? SELLER_VIEW_STATE.unavailable
            : SELLER_VIEW_STATE.ready;

    const sellerView = useMemo(() => ({
        title: LABEL.sellerProfile,
        Icon: StorefrontOutlinedIcon,
        state: sellerViewState,
        loadingText: LABEL.sellerProfileLoading,
        unavailableText: LABEL.sellerProfileUnavailable,
        rows: seller.sellerProfile === null
            ? []
            : [
                {
                    key: LABEL.sellerBusinessName,
                    label: LABEL.sellerBusinessName,
                    value: displayValue(
                        seller.sellerProfile.businessName
                        ?? seller.sellerProfile.firm,
                    ),
                    sm: UI.grid.full,
                },
                {
                    key: LABEL.sellerName,
                    label: LABEL.sellerName,
                    value: formatSellerName(seller.sellerProfile),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerUsername,
                    label: LABEL.sellerUsername,
                    value: displayValue(seller.sellerProfile.username),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerLocation,
                    label: LABEL.sellerLocation,
                    value: formatSellerLocation(seller.sellerProfile),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerCountry,
                    label: LABEL.sellerCountry,
                    value: displayValue(seller.sellerProfile.country),
                    sm: UI.grid.half,
                },
            ],
    }), [seller.sellerProfile, sellerViewState]);

    const definition = DIALOG_DEFINITIONS[openDialog] ?? DEFAULT_DIALOG_DEFINITION;

    return {
        dialog: {
            open: openDialog !== null,
            type: openDialog,
            title: definition.title,
            Icon: definition.Icon,
            maxWidth: definition.maxWidth,
            closeLabel: LABEL.closeDialog,
            onClose: close,
            details,
            comment,
            documents: {
                title: LABEL.documents,
                Icon: DescriptionOutlinedIcon,
                rows: documents,
            },
            seller: sellerView,
        },
        actions: [
            {
                key: INFO_DIALOG.details,
                label: LABEL.details,
                Icon: InfoOutlinedIcon,
                onClick: openDetails,
            },
            {
                key: INFO_DIALOG.comment,
                label: LABEL.comment,
                Icon: CommentOutlinedIcon,
                onClick: openComment,
            },
            {
                key: INFO_DIALOG.documents,
                label: LABEL.documents,
                Icon: DescriptionOutlinedIcon,
                onClick: openDocuments,
            },
            {
                key: INFO_DIALOG.seller,
                label: LABEL.seller,
                Icon: StorefrontOutlinedIcon,
                onClick: openSeller,
            },
        ],
    };
};

const useAuctionViewModel = ({
    appAPI,
    contextAPI,
    auctionId,
}) => {
    const accessToken = contextAPI.password;
    const username = contextAPI.username;
    const userId = contextAPI.id;
    const showMessage = appAPI.showMessage;

    const resource = useAuctionResource({
        accessToken,
        auctionId,
        userId,
        showMessage,
    });
    const buyerPermission = useBuyerPermission({
        userId,
        username,
        accessToken,
        showMessage,
    });
    const status = useAuctionStatusController({
        accessToken,
        auctionId,
        userId,
        auction: resource.auction,
        showMessage,
    });
    const handleDocumentDownload = useDocumentDownload(showMessage);
    const sellerId = resource.lead?.authorId ?? null;
    const isOwnAuction = idsAreEqual(sellerId, userId);
    const bid = useBidController({
        accessToken,
        auction: resource.auction,
        userId,
        canBuy: buyerPermission.canBuy,
        canBuyLoading: buyerPermission.isLoading,
        currentStatus: status.currentStatus,
        highestBid: status.highestBid,
        statusLoading: status.isLoading,
        isOwnAuction,
        refreshStatus: status.refresh,
        markAsHighestBidder: status.markAsHighestBidder,
        showMessage,
    });
    const dialogController = useAuctionDialogController({
        auction: resource.auction,
        lead: resource.lead,
        currentStatus: status.currentStatus,
        sellerId,
        accessToken,
        showMessage,
        handleDocumentDownload,
    });

    const viewState = resource.isLoading
        ? VIEW_STATE.loading
        : resource.hasAuctionError || resource.auction === null
            ? VIEW_STATE.error
            : resource.hasLeadError || resource.lead === null
                ? VIEW_STATE.unavailable
                : VIEW_STATE.ready;

    const mapCoordinates = getMapCoordinates(resource.lead);
    const isAuctionActive = bid.isAuctionActive;
    const hasHighestBid = status.highestBid !== null
        && status.highestBid !== undefined;
    const statusColor = isAuctionActive && status.isHighestBidder
        ? PRIMARY_COLOR
        : "gray";
    const minimumBidText = isAuctionActive
        && !hasHighestBid
        && resource.auction?.minBid !== null
        && resource.auction?.minBid !== undefined
        ? `${LABEL.minimumBid}: ${formatCurrency(resource.auction.minBid)}`
        : VALUE.empty;
    const highestBidLabel = isAuctionActive && hasHighestBid
        ? LABEL.currentHighestBid
        : VALUE.empty;

    return {
        state: viewState,
        loading: {},
        error: {
            message: resource.auctionErrorMessage || MESSAGE.auctionLoadFailed,
        },
        unavailable: {
            message: MESSAGE.notAvailable,
        },
        ready: {
            map: {
                latitude: mapCoordinates.latitude,
                longitude: mapCoordinates.longitude,
                zoom: CONFIG.mapZoom,
                height: CONFIG.mapHeight,
            },
            header: {
                title: buildAuctionTitle(resource.lead),
                statusLabel: getStatusLabel(status.currentStatus),
                typeLabel: LABEL.auction,
            },
            warning: {
                visible: status.warningMessage !== VALUE.empty,
                message: status.warningMessage,
            },
            status: {
                highestBidLabel,
                valueStyle: {
                    ...STYLE.statusValue,
                    color: statusColor,
                },
                minimumBidStyle: {
                    ...STYLE.minimumBid,
                    color: statusColor,
                },
                value: getStatusDisplayValue(
                    status.currentStatus,
                    status.highestBid,
                ),
                minimumBidText,
                color: statusColor,
                showProgress: isAuctionActive,
                progress: calculateProgress(
                    resource.auction?.startTimestamp,
                    resource.auction?.duration,
                ),
                timeText: getStatusTimeText(
                    status.currentStatus,
                    resource.auction,
                ),
            },
            actions: {
                info: dialogController.actions,
                bidInput: {
                    value: bid.currentBid,
                    label: LABEL.yourBid,
                    disabled: bid.inputDisabled,
                    helperText: bid.helperText,
                    onChange: bid.handleChange,
                    onKeyDown: bid.handleKeyDown,
                },
                refresh: {
                    label: LABEL.refreshStatus,
                    disabled: status.isLoading,
                    onClick: status.refresh,
                },
                bid: {
                    label: LABEL.bid,
                    disabled: bid.buttonDisabled,
                    onClick: bid.submit,
                },
            },
            dialog: dialogController.dialog,
        },
    };
};

function InfoRow({ model }) {
    return (
        <Grid
            item={UI.boolean.true}
            xs={UI.grid.full}
            sm={model.sm}
        >
            <Typography
                variant={UI.variant.caption}
                color={UI.color.secondaryText}
                sx={STYLE.infoLabel}
            >
                {model.label}
            </Typography>

            <Typography
                variant={UI.variant.body1}
                sx={STYLE.infoValue}
            >
                {model.value}
            </Typography>
        </Grid>
    );
}

function InfoRows({ rows }) {
    return rows.map((row) => (
        <InfoRow
            key={row.key}
            model={row}
        />
    ));
}

function OverviewCard({ model, children }) {
    const Icon = model.Icon;

    return (
        <Paper
            variant={UI.variant.outlined}
            sx={COLOR_STYLE.overviewPaper}
        >
            <CardContent sx={STYLE.overviewContent}>
                <Stack
                    direction={UI.stack.row}
                    alignItems={UI.stack.center}
                    spacing={UI.stack.cardSpacing}
                    sx={STYLE.cardHeader}
                >
                    <Box sx={COLOR_STYLE.cardIcon}>
                        <Icon fontSize={UI.size.small} />
                    </Box>

                    <Typography
                        variant={UI.variant.h6}
                        sx={STYLE.cardTitle}
                    >
                        {model.title}
                    </Typography>
                </Stack>

                {children}
            </CardContent>
        </Paper>
    );
}

function AuctionDetailsView({ model }) {
    return (
        <OverviewCard model={model}>
            <Grid
                container={UI.boolean.true}
                spacing={UI.grid.detailsSpacing}
            >
                <InfoRows rows={model.rows} />
            </Grid>
        </OverviewCard>
    );
}

function CommentDetailsView({ model }) {
    return (
        <OverviewCard model={model}>
            <Typography
                variant={UI.variant.body1}
                component={UI.component.div}
                sx={STYLE.commentText}
            >
                {model.text}
            </Typography>
        </OverviewCard>
    );
}

function DocumentRow({ model }) {
    return (
        <Stack
            direction={RESPONSIVE_DOCUMENT_DIRECTION}
            alignItems={RESPONSIVE_DOCUMENT_ALIGNMENT}
            justifyContent={UI.stack.spaceBetween}
            spacing={UI.stack.documentSpacing}
            sx={STYLE.documentRow}
        >
            <Box>
                <Typography fontWeight={STYLE.cardTitle.fontWeight}>
                    {model.title}
                </Typography>

                <Typography
                    variant={UI.variant.body2}
                    color={UI.color.secondaryText}
                >
                    {model.typeText}
                </Typography>
            </Box>

            <Button
                variant={UI.variant.outlined}
                startIcon={<DownloadOutlinedIcon />}
                disabled={model.disabled}
                onClick={model.onDownload}
                sx={COLOR_STYLE.documentButton}
            >
                {model.downloadLabel}
            </Button>
        </Stack>
    );
}

const renderDocumentRow = (row) => (
    <DocumentRow
        key={row.key}
        model={row}
    />
);

function DocumentRows({ rows }) {
    const renderedRows = rows.map(renderDocumentRow);

    return (
        <Stack divider={<Divider />}>
            {renderedRows}
        </Stack>
    );
}

function DocumentDownloadsView({ model }) {
    return (
        <OverviewCard model={model}>
            <DocumentRows rows={model.rows} />
        </OverviewCard>
    );
}

function SellerLoadingView({ model }) {
    return (
        <Box sx={STYLE.sellerLoading}>
            <CircularProgress size={UI.progress.sellerSize} />
            <Typography color={UI.color.secondaryText}>
                {model.loadingText}
            </Typography>
        </Box>
    );
}

function SellerUnavailableView({ model }) {
    return (
        <Typography color={UI.color.secondaryText}>
            {model.unavailableText}
        </Typography>
    );
}

function SellerReadyView({ model }) {
    return (
        <Grid
            container={UI.boolean.true}
            spacing={UI.grid.detailsSpacing}
        >
            <InfoRows rows={model.rows} />
        </Grid>
    );
}

const SELLER_VIEW_COMPONENT = Object.freeze({
    [SELLER_VIEW_STATE.loading]: SellerLoadingView,
    [SELLER_VIEW_STATE.unavailable]: SellerUnavailableView,
    [SELLER_VIEW_STATE.ready]: SellerReadyView,
});

function SellerProfileView({ model }) {
    const Content = SELLER_VIEW_COMPONENT[model.state];

    return (
        <OverviewCard model={model}>
            <Content model={model} />
        </OverviewCard>
    );
}

const DIALOG_CONTENT_COMPONENT = Object.freeze({
    [INFO_DIALOG.details]: AuctionDetailsView,
    [INFO_DIALOG.comment]: CommentDetailsView,
    [INFO_DIALOG.documents]: DocumentDownloadsView,
    [INFO_DIALOG.seller]: SellerProfileView,
});

const DIALOG_CONTENT_MODEL_KEY = Object.freeze({
    [INFO_DIALOG.details]: INFO_DIALOG.details,
    [INFO_DIALOG.comment]: INFO_DIALOG.comment,
    [INFO_DIALOG.documents]: INFO_DIALOG.documents,
    [INFO_DIALOG.seller]: INFO_DIALOG.seller,
});

function AuctionDialogContent({ model }) {
    const Content = DIALOG_CONTENT_COMPONENT[model.type] ?? AuctionDetailsView;
    const modelKey = DIALOG_CONTENT_MODEL_KEY[model.type] ?? INFO_DIALOG.details;
    const contentModel = model[modelKey];

    return (
        <Content model={contentModel} />
    );
}

function AuctionInfoDialog({ model }) {
    const Icon = model.Icon;

    return (
        <Dialog
            open={model.open}
            onClose={model.onClose}
            fullWidth={UI.boolean.true}
            maxWidth={model.maxWidth}
            scroll={UI.dialog.scrollPaper}
            PaperProps={DIALOG_PAPER_PROPS}
        >
            <DialogTitle sx={COLOR_STYLE.dialogTitle}>
                <Stack
                    direction={UI.stack.row}
                    alignItems={UI.stack.center}
                    spacing={UI.stack.cardSpacing}
                    sx={STYLE.dialogTitleContent}
                >
                    <Box sx={COLOR_STYLE.dialogTitleIcon}>
                        <Icon fontSize={UI.size.small} />
                    </Box>

                    <Typography
                        component={UI.component.span}
                        sx={STYLE.dialogTitleText}
                    >
                        {model.title}
                    </Typography>
                </Stack>

                <IconButton
                    onClick={model.onClose}
                    aria-label={model.closeLabel}
                    sx={COLOR_STYLE.dialogCloseButton}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers={UI.boolean.true}
                sx={COLOR_STYLE.dialogContent}
            >
                <AuctionDialogContent model={model} />
            </DialogContent>
        </Dialog>
    );
}

function AuctionHeader({ model }) {
    return (
        <Box sx={COLOR_STYLE.titleBar}>
            <Typography
                component={UI.component.span}
                sx={STYLE.titleText}
            >
                {model.title}
            </Typography>

            <Box sx={STYLE.badgeContainer}>
                <Box
                    component={UI.component.span}
                    sx={COLOR_STYLE.headerBadge}
                >
                    {model.statusLabel}
                </Box>

                <Box
                    component={UI.component.span}
                    sx={COLOR_STYLE.headerBadge}
                >
                    {model.typeLabel}
                </Box>
            </Box>
        </Box>
    );
}

function StatusWarning({ model }) {
    if (!model.visible) {
        return null;
    }

    return (
        <Alert
            severity={UI.severity.warning}
            sx={STYLE.statusWarning}
        >
            {model.message}
        </Alert>
    );
}

function AuctionProgress({ model }) {
    if (!model.showProgress) {
        return null;
    }

    return (
        <CircularProgress
            sx={COLOR_STYLE.progress}
            value={model.progress}
            size={UI.progress.auctionSize}
            thickness={UI.progress.auctionThickness}
            variant={UI.variant.determinate}
        />
    );
}

function AuctionStatus({ model }) {
    return (
        <CardContent sx={STYLE.content}>
            <Typography
                sx={STYLE.statusLabel}
                color={UI.color.secondaryText}
                gutterBottom={UI.boolean.true}
            >
                {model.highestBidLabel}
            </Typography>

            <Typography
                variant={UI.variant.h5}
                component={UI.component.div}
                sx={model.valueStyle}
            >
                {model.value}
            </Typography>

            <Typography
                component={UI.component.div}
                sx={model.minimumBidStyle}
            >
                {model.minimumBidText}
            </Typography>

            <Box sx={STYLE.timeContainer}>
                <AuctionProgress model={model} />

                <Typography
                    sx={STYLE.timeText}
                    color={UI.color.secondaryText}
                    gutterBottom={UI.boolean.true}
                >
                    {model.timeText}
                </Typography>
            </Box>
        </CardContent>
    );
}

function InfoActionButton({ model }) {
    const Icon = model.Icon;

    return (
        <Button
            size={UI.size.small}
            startIcon={<Icon />}
            sx={STYLE.infoButton}
            onClick={model.onClick}
        >
            {model.label}
        </Button>
    );
}

const renderInfoAction = (action) => (
    <InfoActionButton
        key={action.key}
        model={action}
    />
);

function InfoActions({ actions }) {
    const renderedActions = actions.map(renderInfoAction);

    return (
        <Box sx={STYLE.infoActions}>
            {renderedActions}
        </Box>
    );
}

function BidInput({ model }) {
    return (
        <TextField
            value={model.value}
            label={model.label}
            variant={UI.variant.standard}
            sx={COLOR_STYLE.primaryAction}
            disabled={model.disabled}
            helperText={model.helperText}
            onChange={model.onChange}
            onKeyDown={model.onKeyDown}
        />
    );
}

function BidButtons({ refresh, bid }) {
    return (
        <>
            <IconButton
                sx={COLOR_STYLE.actionButton}
                onClick={refresh.onClick}
                disabled={refresh.disabled}
                aria-label={refresh.label}
            >
                <AutorenewIcon />
            </IconButton>

            <Button
                startIcon={<GavelIcon />}
                sx={COLOR_STYLE.actionButton}
                onClick={bid.onClick}
                disabled={bid.disabled}
            >
                {bid.label}
            </Button>
        </>
    );
}

function AuctionActions({ model }) {
    return (
        <CardContent sx={STYLE.actionContent}>
            <Grid
                container={UI.boolean.true}
                spacing={UI.grid.contentSpacing}
            >
                <Grid
                    item={UI.boolean.true}
                    xs={UI.grid.full}
                >
                    <InfoActions actions={model.info} />
                </Grid>

                <Grid
                    item={UI.boolean.true}
                    xs={UI.grid.bidInput}
                >
                    <BidInput model={model.bidInput} />
                </Grid>

                <Grid
                    item={UI.boolean.true}
                    xs={UI.grid.full}
                >
                    <BidButtons
                        refresh={model.refresh}
                        bid={model.bid}
                    />
                </Grid>
            </Grid>
        </CardContent>
    );
}

function LoadingView() {
    return (
        <LinearProgress />
    );
}

function ErrorView({ model }) {
    return (
        <Alert
            severity={UI.severity.error}
            sx={STYLE.terminalError}
        >
            {model.message}
        </Alert>
    );
}

function UnavailableView({ model }) {
    return (
        <Typography>
            {model.message}
        </Typography>
    );
}

function ReadyView({ model }) {
    return (
        <>
            <Paper
                variant={UI.variant.outlined}
                sx={COLOR_STYLE.paper}
            >
                <MemoizedStaticMap
                    latitude={model.map.latitude}
                    longitude={model.map.longitude}
                    zoom={model.map.zoom}
                    height={model.map.height}
                />

                <AuctionHeader model={model.header} />
                <StatusWarning model={model.warning} />
                <AuctionStatus model={model.status} />
                <AuctionActions model={model.actions} />
            </Paper>

            <AuctionInfoDialog model={model.dialog} />
        </>
    );
}

const PAGE_COMPONENT = Object.freeze({
    [VIEW_STATE.loading]: LoadingView,
    [VIEW_STATE.error]: ErrorView,
    [VIEW_STATE.unavailable]: UnavailableView,
    [VIEW_STATE.ready]: ReadyView,
});

export default function Auction({
    appAPI,
    contextAPI,
    auctionId,
}) {
    const viewModel = useAuctionViewModel({
        appAPI,
        contextAPI,
        auctionId,
    });
    const Page = PAGE_COMPONENT[viewModel.state];
    const pageModel = viewModel[viewModel.state];

    return (
        <Page model={pageModel} />
    );
}